import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { listInterviewSlots, getCandidateProfile } from '../graphql/queries';
import { updateInterviewSlot } from '../graphql/mutations';
import { isValidTokenFormat, TOKEN_STATUS } from '../utils/tokenUtils';
import Interview from './Interview'; // Original interview component
import InterviewRefactored from './InterviewRefactored'; // New refactored interview component

/**
 * 🚀 TESTING SETUP FOR REFACTORED INTERVIEW
 * 
 * To test the new refactored interview component:
 * 1. Set USE_REFACTORED_INTERVIEW = true (below)
 * 2. Generate interview links through JobDetailsList page
 * 3. Test the refactored version with the green indicator
 * 4. When satisfied, change this flag to false to revert to original
 * 
 * When ready to make refactored version primary:
 * - Set USE_REFACTORED_INTERVIEW = true permanently
 * - Or rename InterviewRefactored.js → Interview.js and Interview.js → InterviewOld.js
 */

// 🚀 TESTING FLAG: Set to true to use the refactored interview component
const USE_REFACTORED_INTERVIEW = true;

const PublicInterview = () => {
  const { accessToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interviewSlot, setInterviewSlot] = useState(null);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [isValidating, setIsValidating] = useState(true);
  const [showStartPage, setShowStartPage] = useState(true);
  const [isStartingInterview, setIsStartingInterview] = useState(false);

  const validateTokenAndLoadInterview = useCallback(async () => {
    try {
      setIsValidating(true);
      
      // Find the interview slot using the ID as access token
      let interviewSlotData = null;
      let nextToken = null;
      
      do {
        const response = await API.graphql(
          graphqlOperation(listInterviewSlots, {
            filter: {
              id: { eq: accessToken }
            },
            limit: 100,
            nextToken: nextToken
          })
        );
        
        const items = response.data.listInterviewSlots.items;
        if (items.length > 0) {
          interviewSlotData = items[0];
          break;
        }
        
        nextToken = response.data.listInterviewSlots.nextToken;
      } while (nextToken);

      if (!interviewSlotData) {
        setError('Interview not found or access token is invalid');
        setLoading(false);
        return;
      }

      // Check token status using existing status field
      if (interviewSlotData.status === TOKEN_STATUS.USED) {
        setError('This interview link has already been used and cannot be accessed again');
        setLoading(false);
        return;
      }

      if (interviewSlotData.status === TOKEN_STATUS.EXPIRED) {
        setError('This interview link has expired');
        setLoading(false);
        return;
      }      // Load candidate profile (handle both registered and unregistered candidates)
      let candidateData = null;
      
      // Parse meetingConfig first to check for unregistered flag
      let meetingConfig = null;
      try {
        meetingConfig = JSON.parse(interviewSlotData.meetingConfig || '{}');
      } catch (parseError) {
        console.error('Error parsing meetingConfig:', parseError);
        meetingConfig = {};
      }
      
      if (interviewSlotData.candidateProfileID) {
        // Load from CandidateProfile table
        const candidateResponse = await API.graphql(
          graphqlOperation(getCandidateProfile, {
            id: interviewSlotData.candidateProfileID
          })
        );
        candidateData = candidateResponse.data.getCandidateProfile;
        
        // Add meetingConfig to candidate data (especially important for demo interviews)
        if (meetingConfig && (meetingConfig.isUnregistered || meetingConfig.numberOfQuestions)) {
          candidateData = {
            ...candidateData,
            isUnregistered: meetingConfig.isUnregistered || false,
            meetingConfig: meetingConfig
          };
        }
      } else {
        // Unregistered candidate - extract from meetingConfig
        if (meetingConfig && meetingConfig.isUnregistered) {
          // Create a minimal candidate profile object for unregistered candidates
          candidateData = {
            id: `unregistered-${interviewSlotData.id}`,
            email: interviewSlotData.candidateEmail,
            fullName: meetingConfig.candidateName || 'Anonymous Candidate',
            private_Candidate: true,
            isPublic: false,
            // Add other minimal fields as needed
            workStatus: 'Unregistered Candidate',
            description: `Interview for ${meetingConfig.jobTitle || 'Private Screening'}`,
            // Mark as unregistered for special handling
            isUnregistered: true,
            meetingConfig: meetingConfig
          };
        }
      }

      if (!candidateData) {
        setError('Candidate information not found');
        setLoading(false);
        return;
      }

      // Store the data but don't start interview yet - wait for user to click start button
      setInterviewSlot(interviewSlotData);
      setCandidateProfile(candidateData);

    } catch (error) {
      console.error('Error validating token and loading interview:', error);
      setError('Failed to load interview. Please check your link and try again.');
    } finally {
      setLoading(false);
      setIsValidating(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      setError('No access token provided');
      setLoading(false);
      return;
    }

    if (!isValidTokenFormat(accessToken)) {
      setError('Invalid access token format');
      setLoading(false);
      return;
    }

    validateTokenAndLoadInterview();
  }, [accessToken, validateTokenAndLoadInterview]);

  // Clean up localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('publicInterviewMode');
    };
  }, []);
  const startInterviewSession = async () => {
    try {
      setIsStartingInterview(true);
      
      // Mark token as used by updating status to USED
      await API.graphql(
        graphqlOperation(updateInterviewSlot, {
          input: {
            id: interviewSlot.id,
            status: TOKEN_STATUS.USED
          }
        })
      );

      // Store candidate info in localStorage for the interview component
      localStorage.setItem('candidateProfileEmailID', candidateProfile.email);
      
      // Store complete candidate data for Interview.js to use
      localStorage.setItem('publicInterviewCandidateData', JSON.stringify(candidateProfile));
      localStorage.setItem('publicInterviewSlotData', JSON.stringify(interviewSlot));
      
      // For unregistered candidates, use interview slot ID as profile ID
      const profileId = candidateProfile.isUnregistered 
        ? `unregistered-${interviewSlot.id}` 
        : candidateProfile.id;
      
      localStorage.setItem('candidateProfileID', profileId);
      localStorage.setItem('publicInterviewMode', 'true');
      localStorage.setItem('interviewSlotID', interviewSlot.id);
      
      // Store additional info for unregistered candidates
      if (candidateProfile.isUnregistered) {
        localStorage.setItem('isUnregisteredCandidate', 'true');
        localStorage.setItem('candidateFullName', candidateProfile.fullName);
        if (candidateProfile.meetingConfig) {
          localStorage.setItem('unregisteredCandidateConfig', JSON.stringify(candidateProfile.meetingConfig));
          
          // Extract number of questions if available
          if (candidateProfile.meetingConfig.numberOfQuestions) {
             localStorage.setItem('interviewNumberOfQuestions', candidateProfile.meetingConfig.numberOfQuestions);
          }
        }
      }

      // Handle screening round detection and storage
      if (candidateProfile.meetingConfig) {
        const meetingConfig = candidateProfile.meetingConfig;
        
        // Check if this is a screening round interview
        if (meetingConfig.isScreeningRound || meetingConfig.interviewType === 'screening') {
          localStorage.setItem('isScreeningRound', 'true');
          console.log('🎯 Screening round interview detected');
          
          // Store number of questions for screening (usually 5)
          if (meetingConfig.numberOfQuestions) {
            localStorage.setItem('interviewNumberOfQuestions', String(meetingConfig.numberOfQuestions));
          }
        }
      }

      // console.log('Public interview session started. Stored in localStorage:', {
      //   candidateEmail: candidateProfile.email,
      //   candidateFullName: candidateProfile.fullName,
      //   interviewSlotID: interviewSlot.id,
      //   isUnregistered: candidateProfile.isUnregistered || false
      // });

      // Hide the start page and show the interview
      setShowStartPage(false);

    } catch (error) {
      console.error('Error starting interview session:', error);
      setError('Failed to start interview session. Please try again.');
      setIsStartingInterview(false);
    }
  };

  if (loading || isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-400 mx-auto mb-6"></div>
          <p className="text-white text-lg">Validating your interview access...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-purple-900 to-indigo-900">
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-screen">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-2xl p-8 text-center">
            <div className="text-red-500 mb-6">
              <svg className="mx-auto h-20 w-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>            <h2 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-8">{error}</p>
            <p className="text-gray-500 text-sm">Please contact the company that sent you this link for assistance.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!interviewSlot || !candidateProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg">Failed to load interview data</p>
        </div>
      </div>
    );
  }

  // Show start page with beautiful design
  if (showStartPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        {/* Version Indicator */}

        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-6">
                <h1 className="text-3xl font-bold text-white text-center">Welcome to Your Interview</h1>
                <p className="text-purple-100 text-center mt-2">GetScreened AI Interview Platform</p>
              </div>

              {/* Content */}
              <div className="px-8 py-10">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 mb-4">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Hello, {candidateProfile.fullName}!</h2>
                  <p className="text-gray-600">You're all set to begin your AI-powered interview session.</p>
                </div>

                {/* Instructions */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Before You Start:</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 text-white text-sm font-medium mr-3 mt-0.5">1</span>
                      <span>Ensure you're in a quiet environment with good lighting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 text-white text-sm font-medium mr-3 mt-0.5">2</span>
                      <span>Check your camera and microphone permissions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 text-white text-sm font-medium mr-3 mt-0.5">3</span>
                      <span>Make sure you have a stable internet connection</span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-500 text-white text-sm font-medium mr-3 mt-0.5">4</span>
                      <span>This interview link can only be used once</span>
                    </li>
                  </ul>
                </div>

                {/* Interview Details */}
                <div className="border border-gray-200 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Details:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-900">Candidate:</span>
                      <p className="text-gray-600">{candidateProfile.fullName}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Email:</span>
                      <p className="text-gray-600">{candidateProfile.email}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Session ID:</span>
                      <p className="text-gray-600 font-mono">{interviewSlot.id.slice(0, 8)}...</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Type:</span>
                      <p className="text-gray-600">AI-Powered Interview</p>
                    </div>
                  </div>
                </div>

                {/* Start Button */}
                <div className="text-center">
                  <button
                    onClick={startInterviewSession}
                    disabled={isStartingInterview}
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold text-lg rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-purple-500 focus:ring-opacity-50 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isStartingInterview ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Starting Interview...
                      </>
                    ) : (
                      <>
                        <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Start Interview Now
                      </>
                    )}
                  </button>
                  <p className="text-gray-500 text-sm mt-4">Click the button above when you're ready to begin</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show the actual Interview component after start button is clicked
  return (
    <div className="min-h-screen">
      {/* Version Indicator for Interview Page */}

      
      {USE_REFACTORED_INTERVIEW ? <InterviewRefactored /> : <Interview />}
    </div>
  );
};

export default PublicInterview;
